import { FC, ReactNode } from 'react'
import styled from '@emotion/styled/macro'
import { Box, Typography } from '@mui/material'
import theme from 'theme'
import { useThemeColors } from 'hooks/useThemeColors'
import { LoadingSpinner } from '../icons'

interface Props {
  value: number
  hideValue?: boolean
  isActive?: boolean
  changeIndicator?: ReactNode
}

const Container = styled(Box)`
  position: relative;
  width: 253px;
  height: 145px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ValueWrapper = styled(Box)`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  .value {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.epic};
    line-height: 40px;
    letter-spacing: 0.5px;
    text-align: center;
    margin-bottom: 12px;
  }

  .indicators-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  }
`

const OverPerformanceIndicator = styled(Box)`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-top: 4px;

  .icon-text {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .over-value {
    color: #ffe082;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
  }

  .over-text {
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`

const CategoryHalfDonut: FC<Props> = ({ value, hideValue, isActive = true, changeIndicator }) => {
  const getColor = useThemeColors()
  // Matches the Figma half-donut: 253×127 with an elliptical arc (rx=115.5,
  // ry=105) so the donut sits flatter than a true half-circle and the stroke
  // doesn't get clipped at the top of the SVG.
  const width = 253
  const strokeWidth = 22
  const rx = (width - strokeWidth) / 2
  const ry = 105
  const svgHeight = ry + strokeWidth
  const cy = svgHeight - strokeWidth / 2
  const arcPath = `M ${strokeWidth / 2},${cy} A ${rx},${ry} 0 0,1 ${width - strokeWidth / 2},${cy}`

  // Half-perimeter of the ellipse via Ramanujan's approximation, used to
  // size the progress dasharray correctly on the elliptical arc.
  const h = Math.pow((rx - ry) / (rx + ry), 2)
  const circumference = (Math.PI * (rx + ry) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)))) / 2

  const clampedValue = Math.min(value, 100)
  const overValue = Math.max(value - 100, 0)
  const progressLength = (clampedValue / 100) * circumference

  return (
    <Container>
      <svg width={width} height={svgHeight} viewBox={`0 0 ${width} ${svgHeight}`} fill="none">
        <path
          d={arcPath}
          fill="none"
          stroke={isActive ? getColor('gauge-track') : getColor('card-fill-tertiary')}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {clampedValue > 0 && (
          <path
            d={arcPath}
            fill="none"
            stroke={getColor('cta')}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={
              clampedValue === 100 ? `${circumference + 50} 0` : `${progressLength} ${circumference * 2}`
            }
            style={{ transition: 'stroke-dasharray 0.8s ease-in-out' }}
          />
        )}
      </svg>
      <ValueWrapper>
        {hideValue ? <LoadingSpinner /> : <Typography className="value">{value}%</Typography>}
        {!hideValue && (changeIndicator || overValue > 0) && (
          <div className="indicators-wrapper">
            {changeIndicator}
            {overValue > 0 && (
              <OverPerformanceIndicator>
                <Box className="icon-text">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                      fill={theme.baseColors.info[30]}
                    />
                  </svg>
                  <Typography className="over-value">{overValue.toFixed(0)}%</Typography>
                </Box>
                <Typography className="over-text">Above Target</Typography>
              </OverPerformanceIndicator>
            )}
          </div>
        )}
      </ValueWrapper>
    </Container>
  )
}

export default CategoryHalfDonut
